// =============================================
// YahooChart.jsx — Yahoo Finance 데이터 기반 캔들스틱 차트
// =============================================
// 주식·지수 전용 차트 컴포넌트야.
// 코인은 TradingViewWidget을 써. (실시간 스트리밍 가능)
// 주식·지수는 이 컴포넌트를 써. (yfinance → lightweight-charts)
//
// 동작 흐름:
//   ① ticker prop 받음  (예: "005930.KS", "^KS11", "^DJI", "GC=F", "AAPL")
//   ② 백엔드 /api/chart/stock/{ticker} 호출
//      → 백엔드가 yfinance (Yahoo Finance)로 OHLCV 데이터 가져옴
//   ③ TradingViewChart.jsx (lightweight-charts)로 캔들차트 렌더링
//
// Yahoo Finance 티커 형식:
//   한국 주식:  "005930.KS" (코스피), "035420.KQ" (코스닥)
//   한국 지수:  "^KS11" (KOSPI), "^KQ11" (KOSDAQ)
//   미국 주식:  "AAPL", "MSFT", "UNH" 등 (거래소 접두어 불필요)
//   글로벌 지수: "^DJI" (다우), "^IXIC" (나스닥)
//   상품 선물:  "GC=F" (금), "SI=F" (은)
//
// 사용 예시:
//   <YahooChart ticker="005930.KS" interval="1d" height={800} />
//   <YahooChart ticker="^KS11"     interval="1w" height={600} />
// =============================================

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import TradingViewChart from './TradingViewChart';

const API_URL = 'http://localhost:8000';


function YahooChart({ ticker, interval = '1d', height = 800 }) {
  // candles: OHLCV 배열 — TradingViewChart에 전달
  const [candles,  setCandles]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  // =============================================
  // 데이터 fetch — ticker 또는 interval이 바뀔 때마다 실행
  // =============================================
  useEffect(() => {
    // 새 요청 시작 → 이전 데이터 초기화
    setLoading(true);
    setErrorMsg(null);
    setCandles([]);

    // 백엔드 API 호출
    // ticker에 특수문자(^, =)가 있으니 encodeURIComponent로 인코딩해야 해
    axios.get(`${API_URL}/api/chart/stock/${encodeURIComponent(ticker)}`, {
      params: { interval },
    })
      .then(r => {
        const data = r.data?.data || [];
        if (data.length === 0) {
          setErrorMsg('데이터가 없어. 시장이 닫혀 있거나 지원하지 않는 티커일 수 있어.');
        } else {
          setCandles(data);
        }
      })
      .catch(e => {
        setErrorMsg(e.response?.data?.detail || e.message || '데이터를 가져오지 못했어.');
      })
      .finally(() => setLoading(false));

  }, [ticker, interval]);  // ticker 또는 interval 바뀌면 재요청


  // ── 로딩 화면 ──
  if (loading) {
    return (
      <div style={{
        height: `${height}px`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '0.75rem',
        backgroundColor: '#1a202c', color: '#4a5568', fontSize: '0.9rem',
      }}>
        {/* 스피너 (초록 계열 — Yahoo Finance 색상) */}
        <div style={{
          width: '36px', height: '36px',
          border: '3px solid #2d3748',
          borderTop: '3px solid #48bb78',  // 초록
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <span>차트 데이터 불러오는 중...</span>
        <span style={{ fontSize: '0.75rem', color: '#4a5568' }}>
          {ticker} · Yahoo Finance
        </span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── 에러 화면 ──
  if (errorMsg || candles.length === 0) {
    return (
      <div style={{
        height: `${height}px`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '0.5rem',
        backgroundColor: '#1a202c', color: '#718096', fontSize: '0.85rem',
      }}>
        <span style={{ fontSize: '1.5rem' }}>📊</span>
        <span>차트를 불러오지 못했어</span>
        <span style={{ fontSize: '0.75rem', color: '#4a5568' }}>{ticker}</span>
        {errorMsg && (
          <span style={{ fontSize: '0.72rem', color: '#e53e3e', marginTop: '4px' }}>
            {errorMsg}
          </span>
        )}
      </div>
    );
  }

  // ── 정상: 캔들차트 렌더링 ──
  return (
    <div style={{ backgroundColor: '#1a202c' }}>

      {/* ★ 디버깅 표시줄 — 어떤 티커가 요청 중인지 확인 */}
      <div style={{
        backgroundColor: '#0d1117',
        color:           '#48bb78',
        fontSize:        '0.75rem',
        fontFamily:      'monospace',
        padding:         '5px 14px',
        borderBottom:    '1px solid #2d3748',
        display:         'flex',
        alignItems:      'center',
        gap:             '0.5rem',
      }}>
        <span style={{ color: '#718096' }}>현재 요청 중인 심볼:</span>
        <strong style={{ color: '#68d391', letterSpacing: '0.04em' }}>{ticker}</strong>
        <span style={{ color: '#4a5568', marginLeft: '0.5rem', fontSize: '0.7rem' }}>
          (Yahoo Finance / yfinance 데이터)
        </span>
      </div>

      {/* lightweight-charts 캔들스틱 차트 */}
      <TradingViewChart
        key={`${ticker}-${interval}`}  // ticker·interval 바뀌면 차트 완전 재생성
        data={candles}
        height={height - 28}           // 28px = 디버그 바 높이
      />
    </div>
  );
}

export default YahooChart;
